import Button from '@mui/material/Button'
import Avatar from '@mui/material/Avatar'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import DialogTitle from '@mui/material/DialogTitle'
import Dialog from '@mui/material/Dialog'
import PaymentIcon from '@mui/icons-material/Payment'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { blue, grey } from '@mui/material/colors'
import {
  Box,
  Chip,
  DialogActions,
  DialogContent,
  DialogContentText,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import { IOrderBump } from '../../core/types/OrderBump'
import { useMemo, useRef } from 'react'

export interface MultiSelectedItem {
  id: string
  name: string
  selected: boolean
  object: any
  order?: number
}

export interface MultiSelectedDialogProps {
  open: boolean
  title?: string
  description?: string
  icon?: JSX.Element
  list: MultiSelectedItem[]
  setList: (items: MultiSelectedItem[]) => void
  onClose: () => void
  onSave: (items: MultiSelectedItem[]) => void
}

const MultiSelectedDialog = (props: MultiSelectedDialogProps): JSX.Element => {
  const { open, title, description, icon, list, setList, onClose, onSave } =
    props

  const dragItem = useRef(-1)
  const dragOverItem = useRef(-1)

  const selectedCount = useMemo(
    () => list.filter((i) => i.selected).length,
    [list]
  )

  const handleClose = () => {
    onClose()
  }

  const handleListItemClick = (item: MultiSelectedItem) => {
    let newList: MultiSelectedItem[] = []
    list.map((l) => {
      newList.push({
        id: l.id,
        name: l.name,
        selected: l.id !== item.id ? l.selected : !l.selected,
        object: l.object,
        order: l.selected ? l.order : undefined,
      })
      return true
    })

    let order = 0
    newList.map((it) => {
      if (it.selected) {
        order += 1
        it.order = order
      } else {
        it.order = undefined
      }
      return true
    })

    newList = newList.sort((a, b) => (a.order || 0) - (b.order || 0))
    newList = newList.sort((a, b) =>
      (a.order || 0) === 0 || (b.order || 0) === 0 ? -1 : 0
    )

    setList(newList)
  }

  const handleSave = () => {
    onSave(list)
    onClose()
  }

  const disabledItem = (item: MultiSelectedItem): boolean => {
    if (!item.object || item.object === null) return false
    if (item.selected) return false

    const otherSelected = list.filter((l) => {
      if (!l.object || l.object === null) return true
      return (
        l.selected &&
        (l.object! as IOrderBump).productPrice!.product!.id ===
          (item.object! as IOrderBump).productPrice!.product!.id
      )
    })

    return otherSelected !== undefined && otherSelected.length > 0
  }

  const dragStart = (e: React.DragEvent<HTMLLIElement>, position: number) => {
    dragItem.current = position
  }

  const dragEnter = (e: React.DragEvent<HTMLLIElement>, position: number) => {
    dragOverItem.current = position
  }

  const drop = (e: React.DragEvent<HTMLLIElement>) => {
    let copyListItems = [...list]
    const dragItemContent = copyListItems[dragItem.current]
    copyListItems.splice(dragItem.current, 1)
    copyListItems.splice(dragOverItem.current, 0, dragItemContent)
    dragItem.current = -1
    dragOverItem.current = -1

    let order = 0
    copyListItems.map((it) => {
      if (it.selected) {
        order += 1
        it.order = order
      }
      return true
    })

    copyListItems = copyListItems.sort(
      (a, b) => (a.order || 0) - (b.order || 0)
    )
    copyListItems = copyListItems.sort((a, b) =>
      (a.order || 0) === 0 || (b.order || 0) === 0 ? -1 : 0
    )

    setList(copyListItems)
  }

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Avatar
            sx={{
              bgcolor: blue[50],
              color: blue[700],
              width: 40,
              height: 40,
            }}
          >
            {icon ? icon : <PaymentIcon />}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <DialogTitle sx={{ p: 0, m: 0, lineHeight: 1.2 }}>
              {title || 'Selecione uma opção'}
            </DialogTitle>

            {description ? (
              <DialogContentText sx={{ mt: 0.5 }}>
                {description}
              </DialogContentText>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Marque os itens desejados. Itens selecionados podem ser
                reordenados por arraste.
              </Typography>
            )}

            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 1 }}
              flexWrap="wrap"
              useFlexGap
            >
              <Chip
                size="small"
                label={`${selectedCount} selecionado${selectedCount === 1 ? '' : 's'}`}
                variant="outlined"
              />
              <Chip
                size="small"
                label="Arraste para reordenar"
                variant="outlined"
                sx={{ display: selectedCount > 0 ? 'inline-flex' : 'none' }}
              />
            </Stack>
          </Box>
        </Stack>
      </Box>

      <Divider />

      <DialogContent sx={{ pt: 1.5 }}>
        <List sx={{ pt: 0 }}>
          {list.map((item, index) => {
            const isDisabled = disabledItem(item)

            return (
              <ListItem
                disableGutters
                key={`item${index}`}
                draggable={item.selected}
                onDragStart={(e) => dragStart(e, index)}
                onDragEnter={(e) => dragEnter(e, index)}
                onDragEnd={drop}
                sx={{ mb: 0.75 }}
              >
                <ListItemButton
                  onClick={() => handleListItemClick(item)}
                  disabled={isDisabled}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: item.selected ? blue[200] : grey[200],
                    bgcolor: item.selected ? blue[50] : 'background.paper',
                    '&:hover': {
                      bgcolor: item.selected ? blue[50] : grey[50],
                    },
                    opacity: isDisabled ? 0.6 : 1,
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 48 }}>
                    <Avatar
                      sx={{
                        bgcolor: item.selected ? blue[100] : grey[100],
                        color: item.selected ? blue[700] : grey[700],
                        width: 34,
                        height: 34,
                      }}
                    >
                      {item.selected ? (
                        <CheckCircleIcon fontSize="small" />
                      ) : icon ? (
                        icon
                      ) : (
                        <PaymentIcon fontSize="small" />
                      )}
                    </Avatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ minWidth: 0 }}
                      >
                        {item.order ? (
                          <Chip
                            size="small"
                            label={`#${item.order}`}
                            sx={{
                              height: 22,
                              fontSize: 12,
                              bgcolor: item.selected ? blue[100] : grey[100],
                            }}
                          />
                        ) : null}

                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: item.selected ? 700 : 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={item.name}
                        >
                          {item.name}
                        </Typography>
                      </Stack>
                    }
                    secondary={
                      isDisabled
                        ? 'Já existe um item selecionado para este produto.'
                        : item.selected
                          ? 'Selecionado • arraste para mudar a ordem'
                          : undefined
                    }
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />

                  <Box
                    sx={{
                      ml: 1,
                      display: 'flex',
                      alignItems: 'center',
                      color: item.selected ? blue[700] : grey[500],
                      opacity: item.selected ? 1 : 0.6,
                      cursor: item.selected ? 'grab' : 'default',
                      userSelect: 'none',
                    }}
                    aria-label="Arrastar para reordenar"
                    title={
                      item.selected
                        ? 'Arraste para reordenar'
                        : 'Selecione para poder reordenar'
                    }
                  >
                    <DragIndicatorIcon fontSize="small" />
                  </Box>
                </ListItemButton>
              </ListItem>
            )
          })}
        </List>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" color="inherit" onClick={handleClose}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          autoFocus
          disabled={selectedCount === 0}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default MultiSelectedDialog
